/* core library */
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { useParams } from "react-router-dom";
import moment from "moment";
import { Search, Close } from "@mui/icons-material";
import {
  Autocomplete,
  FormControl,
  TextField,
  MenuItem,
  InputLabel,
  Select,
  Button,
  Divider,
} from "@mui/material";
import _ from "lodash";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { toast } from "react-toastify";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

/* thunks import */
import { getAllClientsWithProjects } from "src/thunks/clientProjects";
import { getAllItemsByProjectId, addNewDispatchOrder, editDispatchOrder, getDispatchOrderByIdAction, getAllDispatchOrderData } from "src/thunks/dispatchOrder";
import { getAllNofificationForDashboard } from "src/thunks/dashboard";

import { addDispatchOrderForAdminValidationSchema } from "src/helpers/yup_schema";
/* images import */
import IconNewRecord from "src/assets/images/icon_newrecord.svg";
import toastMessages from "src/constant/toastMessages.json";
/* const import */
import { GetError, AddingZeroOnTime, isNumber, isValidateMail, convertIsoToLocalDateTime } from "src/constant";
import { clearDepotsDropdown } from "src/slice/dispatchOrder";
import { Loader } from "src/components/loader";

export const DispatchForm = ({ handleClose, editData, getAllDispatchData, currentPage, isDelivery, isCollection }) => {
  /*  hooks veriable */
  const dispatch = useDispatch();

  /* getting value from redux storage using useSelector */
  const { allItemsByDepotId, addNewDispatchOrderLoadingState, getIndividualDispatchOrderData } = useSelector((state) => state.dispatch);
  const stateDashboard = useSelector((state) => state.clientProjects);
  const lookupValues = useSelector((state) => state.lookup.data);
  const { filterAllData } = useSelector((state) => state.goodsin);

  /* component States */
  const [projectArray, setProjectArray] = useState([]);
  const [projectManagersArray, setProjectManagersArray] = useState([]);
  const [DispatchOrderStateData, _DispatchOrderStateData] = useState({
    orderNo: "",
    type: "",
    dueDate: new Date(),
    time1: moment().hour(),
    time2: moment().minutes(),
  });
  const [selectedDepot, setSelectedDepot] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [filteredItemsArray, setFilteredItemsArray] = useState([]);
  const [selectedItemsArray, setSelectedItemsArray] = useState([]);
  const [orderNoError, setOrderNoError] = useState("");
  const [depotSelectError, setDepotSelectError] = useState("");
  const [addressError, setAddressError] = useState({
    street: "",
    town: "",
    country: "",
    postcode: "",
    phone: null,
    driverContact: null,
    email: null,
  });
  const [highlightedRow, setHighlightedRow] = useState(null);
  const [loader,setLoader] = useState(false);



  /* const and let variable declarations  */
  let { id } = useParams(); id = parseInt(id);
  const allClientsWithProjectsWithManagers = stateDashboard?.allClientsWithProjects ? stateDashboard.allClientsWithProjects : [];
  let damageCount = _.filter(selectedItemsArray, (d) => d.condition === "Damaged")?.length || 0;
  let unitsCount = _.sumBy(selectedItemsArray, (d) => d.quantity) || 0;
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const filterData = filterAllData["dispatch"];
  let client = {};
  const newFilteredArray = _.filter(filteredItemsArray, (value) => {
    return value.itemNumber.toLowerCase().includes(searchValue.toLowerCase());
  });
  const projectRef = useRef();

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(addDispatchOrderForAdminValidationSchema),
  });

  /** useeffect for get All client with project data **/
  useEffect(() => {
    dispatch(getAllClientsWithProjects());
    return () => {
      dispatch(clearDepotsDropdown());
    }
  }, []);

  /** useEffect for reset data from form **/
  useEffect(() => {
    if (editData) {
      resetDataForEditRecord();
    }
  }, [JSON.stringify(editData)]);

  /* in this useeffct filter and set project data when any update in projectArray Variable  */
  useEffect(() => {
    const projectData = projectArray.find((item) => item.id == editData?.projectId);
    setProjectManagersArray(projectData?.managers || []);
  }, [projectArray]);

  /* filter client data on any change in allclientwithProjectWithManager variable  */
  useEffect(() => {
    if (allClientsWithProjectsWithManagers?.length > 0 && DispatchOrderStateData.clientId) {
      const clientData = allClientsWithProjectsWithManagers.find((item) => item.id == DispatchOrderStateData.clientId);
      setProjectArray(clientData?.projects || []);
    }
  }, [allClientsWithProjectsWithManagers]);

  /* fill depot relatable data on edit form */
  const loadDepotRelatedData = async () => {
    setLoader(true)
    try {
      let resultData = await dispatch(
        getAllItemsByProjectId(editData?.projectId)
      ).unwrap();
      setLoader(false)
      if (resultData) {
        resultData = resultData.map((depot) => {
          let newDepot = { ...depot };
          newDepot.items = newDepot.items.map((item) => {
            return {
              ...item,
              availableQty: item.currentQuantity - item.onHoldQuantity,
            };
          });
          return newDepot;
        });
        let found = _.find(
          resultData || [],
          (d) => d.depotId === editData?.depotId
        );
        let newData = _.map(found?.items || [], (item) => {
          return {
            ...item,
            condition: lookupValues.find((v) => v.id === item.conditionId).name,
            storage: lookupValues.find((v) => v.id === item.storageId).name,
          };
        });
        setFilteredItemsArray([...newData]);
        // setOriginalItemsArray([...newData])
        let deliveryItems = [...editData?.deliveryItems];
        deliveryItems = deliveryItems.map((item) => {
          return {
            ...item,
            condition: lookupValues?.find((v) => v.id === item.conditionId)
              .name,
            storage: lookupValues?.find((v) => v.id === item.storageId).name,
          };
        });
        deliveryItems = deliveryItems.map((di) => {
          return { ...di, id: di.id ? di.id : di.itemId };
        });
        setSelectedItemsArray(deliveryItems || []);
      }
    } catch (error) {
      setLoader(false)
      // toast.error(error.response.data.detail || "something issue");
    }
  };

  /* function for fill data in form when click edit data button */
  const resetDataForEditRecord = async () => {
    reset({
      name: editData?.contactName || "",
      email: editData?.contactEmail || "",
      phone: editData?.contactPhone || "",
      client: editData?.clientId || "",
      project: editData?.projectId || "",
      projectManager: editData?.projectManager?.id || "",
    });
    setSelectedDepot(editData?.depotId);

    // ADDED 3 LINES TO SHOW TIME LOCAL INSTEAD UTC 
    let localTime = convertIsoToLocalDateTime(`${editData?.dueDate?.split("T")[0]}T${editData?.dueTime ? editData?.dueTime : "00:00:00"}.00Z`, "DD-MMM-YYYY HH:mm")
    let newLocalTime = moment(localTime).format("HH:mm:ss")
    let newLocalDate = moment(localTime).format("YYYY-MM-DD")

    _DispatchOrderStateData({
      ...DispatchOrderStateData,
      clientId: editData?.clientId || "",
      type: lookupValues?.find((lv) => lv.id == editData?.typeId)?.name || "",
      orderNo: editData?.orderNo || "",
      projectId: editData?.projectId || "",
      projectManagerId: editData?.projectManager?.id || "",
      dueDate: newLocalDate ? new Date(newLocalDate) : new Date(),
      time1:
        (newLocalTime?.split(":")[0] &&
          parseInt(newLocalTime?.split(":")[0], 10).toString()) ||
        moment().hour(),
      time2:
        (newLocalTime?.split(":")[1] &&
          parseInt(newLocalTime?.split(":")[1], 10).toString()) ||
        moment().minutes(),
      name: editData?.contactName || "",
      email: editData?.contactEmail || "",
      phone: editData?.contactPhone || "",
      street: editData?.address?.street1 || "",
      town: editData?.address?.city || "",
      country: editData?.address?.country || "",
      postcode: editData?.address?.zip || "",
      status: editData?.statusId,
      vrn: getIndividualDispatchOrderData?.dispatches[0]?.vrn ? getIndividualDispatchOrderData?.dispatches[0]?.vrn : editData?.vrn,
      vehicleModel: getIndividualDispatchOrderData?.dispatches[0]?.vehicleModel ? getIndividualDispatchOrderData?.dispatches[0]?.vehicleModel : editData?.vehicleModel,
      driverName: getIndividualDispatchOrderData?.dispatches[0]?.driverName ? getIndividualDispatchOrderData?.dispatches[0]?.driverName : editData?.driverName,
      driverContact: getIndividualDispatchOrderData?.dispatches[0]?.driverContact ? getIndividualDispatchOrderData?.dispatches[0]?.driverContact : editData?.driverContact,
    });
    loadDepotRelatedData();
  };

  /* function handle number validaion */
  function validateNumber(input) {
    var re = /^[0-9 -]*$/;
    return re.test(input);
  }



  /* set form value to state when any changes in form filed  */
  const allOnChangeHandler = (title, value) => {
    if (title == "email") {
      if (value != "") {
        setAddressError({ ...addressError, email: null });
      } else {
        setAddressError({
          ...addressError,
          email: "Email is Required",
        });
      }
      if (!isValidateMail(value)) {
        setAddressError({
          ...addressError,
          email: "Enter Valid Email",
        });
      }
    }

    if (title == "phone") {
      if (value != "") {
        setAddressError({ ...addressError, phone: null });
      } else {
        setAddressError({
          ...addressError,
          phone: "Phone is Required",
        });
      }
      if (!validateNumber(value)) {
        setAddressError({
          ...addressError,
          phone: "Enter Valid Phone",
        });
      }
    }

    // -----------------
    if (title == "driverContact") {
      if (!validateNumber(value)) {
        setAddressError({
          ...addressError,
          driverContact: "Enter Valid Contact no",
        });
      } else {
        setAddressError({ ...addressError, driverContact: null });
      }
    }

    _DispatchOrderStateData((state) => ({ ...state, [title]: value }));
  };

  /* This method is used to assign a value to the state of a street. */
  const streetChange = (value) => {
    setAddressError({ ...addressError, street: "" });
    allOnChangeHandler("street", value);
  };

  /* This method is used to assign a value to the state of a town. */
  const townChange = (value) => {
    setAddressError({ ...addressError, town: "" });
    allOnChangeHandler("town", value);
  };

  /* This method is used to assign a value to the state of a country. */
  const countryChange = (value) => {
    setAddressError({ ...addressError, country: "" });
    allOnChangeHandler("country", value);
  };

  /* This method is used to assign a value to the state of a postcode. */
  const postcodeChange = (value) => {
    setAddressError({ ...addressError, postcode: "" });
    allOnChangeHandler("postcode", value);
  };

  /* This method is used to assign a value to the state of a postcode. */
  const AllChangeHandler = (label, value) => {
    setAddressError({ ...addressError, [label]: "" });
    allOnChangeHandler(label, value);
  };

  /* this method is use for genrating random number */
  const RandomeReturnNumberGenrate = (string, len, string1, len1) => {
    return string + makeReturnId1(len) + string1 + makeReturnId(len1);
  };

  /* this method is use for genrating random number for return number and this only 1 to 9 charector */
  function makeReturnId1(length) {
    let result = "";
    const characters = "0123456789";
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }

  /* this method is use for genrating random number for return number and this only 1 to 9 and A-Z charactor */
  function makeReturnId(length) {
    let result = "";
    const characters = "0123456789ABCDEFGHIJKLMNOPQRSTVVWXYZ";
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }

  /* This method is call dispatch api for singal record  */
  const getDispatchOrderEditDataFunc = async () => {
    try {
      await dispatch(getDispatchOrderByIdAction({ id: id || "22" })).unwrap();
    } catch (error) {
      toast.error(GetError(error?.response?.data?.errors) || "something issue");
    }
  };

  /* this method is use for check filed value is empty or not if empty so it give error */
  const checkAddress = () => {
    let errors = {};
    ["street", "town", "country", "postcode", "name", "email", "phone"].map(
      (d) => {
        if (!DispatchOrderStateData[`${d}`]) {
          errors[`${d}`] = `Please add ${d} here`;
        } else {
          delete errors[`${d}`];
        }
      }
    );
    return errors;
  };

  /* this method is call when user click on submit button */
  const onDispatchOrderFormSubmitHandler = async (data) => {
    if (selectedItemsArray.length <= 0) {
      toast.error("Please add delivery items here");
    }
    if (
      DispatchOrderStateData.orderNo == "" ||
      DispatchOrderStateData.orderNo == undefined
    ) {
      setOrderNoError("Please add order no here");
    }
    if (selectedDepot == "" || selectedDepot == undefined) {
      setDepotSelectError("Please select depot here");
    }
    let errors = {};
    if (DispatchOrderStateData.type == "Delivery") {
      errors = checkAddress();
      setAddressError({
        ...addressError,
        ...errors,
      });
    } else {
      setAddressError({
        ...addressError,
        street: "",
        town: "",
        country: "",
        postcode: "",
      });
    }
    clearErrors("radio");
    let newData = { ...DispatchOrderStateData };
    let time = moment.utc(new Date(moment(newData.dueDate).format(
      "YYYY-MM-DD"
    ) + ' ' + `${AddingZeroOnTime(
      newData?.time1 || "00"
    )}:${AddingZeroOnTime(newData?.time2 || "00")}:35`));
    newData["dueDate"] = moment(time).format(
      "YYYY-MM-DD"
    );
    newData["dueTime"] = moment(time).format("HH:mm:ss");
    delete newData.time1;
    delete newData.time2;

    newData["typeId"] = lookupValues?.find((v) => v.name == newData.type)?.id;
    newData["contactName"] = DispatchOrderStateData.name;
    newData["contactPhone"] = DispatchOrderStateData.phone.toString();
    newData["contactEmail"] = DispatchOrderStateData.email;
    newData["depotId"] = selectedDepot;
    newData["typeName"] = DispatchOrderStateData.type;
    newData["ClientName"] = data?.client;
    newData["projectName"] = data?.project;
    newData["role"] = userDetails?.roles;
    newData["vrn"] = DispatchOrderStateData.vrn;
    newData["vehicleModel"] = DispatchOrderStateData.vehicleModel;
    newData["driverName"] = DispatchOrderStateData.driverName;
    newData["driverContact"] = DispatchOrderStateData.driverContact;

    const updatedItemsArray =
      selectedItemsArray &&
      selectedItemsArray[0] &&
      selectedItemsArray.map((item, index) => {
        return {
          deliveryItemId: item.deliveryItemId ?? 0,
          deliveryOrderId: item.deliveryOrderId ?? 0,
          itemId: item.id,
          quantity: item.quantity,
        };
      });

    newData["deliveryItems"] = updatedItemsArray;

    newData["address"] = {
      street1: DispatchOrderStateData.street,
      country: DispatchOrderStateData.country,
      city: DispatchOrderStateData.town,
      zip: DispatchOrderStateData.postcode,
    };
    if (Object.keys(errors).length == 0) {
      if (
        !editData &&
        DispatchOrderStateData.orderNo !== "" &&
        selectedDepot !== "" &&
        Object.keys(errors).length === 0 &&
        selectedItemsArray.length > 0
      ) {
        try {
          newData.statusId = newData?.status
            ? newData?.status
            : userDetails?.roles == "SuperAdmin" ||
              userDetails?.roles == "Admin"
              ? 8
              : 7;
          let result = await dispatch(addNewDispatchOrder(newData)).unwrap();
          if (result) {
            setTimeout(() => {
              dispatch(getAllNofificationForDashboard(1, 20));
            }, 2000);
            getAllDispatchData(currentPage, isDelivery, isCollection)
            toast.success(toastMessages.dispatchAdded);
            handleClose();
          }
        } catch (err) {
          toast.error(
            GetError(err?.response?.data?.errors) || "something issue"
          );
        }
      } else if (editData) {
        if (
          DispatchOrderStateData.orderNo !== "" &&
          selectedDepot !== "" &&
          Object.keys(errors).length === 0 &&
          selectedItemsArray.length > 0
        ) {
          try {
            newData.statusId = (userDetails?.roles == "Admin" || userDetails?.roles == "SuperAdmin") && newData?.status == 10 ? 9 : newData?.status;
            let req = {
              ...newData,
              id: id,
              status: newData?.statusId,
              projectId: newData.projectId,
              clientId: newData.clientId,
              ClientName: data?.client,
              depotId: newData.depotId,
              orderNo: newData.orderNo,
              projectManagerId: newData.projectManagerId,
              dueDate: newData.dueDate,
              dueTime: newData.dueTime,
              deliveryItems: newData.deliveryItems,
              typeId: lookupValues?.find((v) => v.name == newData.type)?.id,
              deliveryOrderRemarks: "",
            };
            let result = await dispatch(
              editDispatchOrder({
                ...req,
              })
            ).unwrap();
            if (result) {
              setTimeout(() => {
                dispatch(getAllNofificationForDashboard(1, 20));
              }, 2000);
              getDispatchOrderEditDataFunc();
              toast.success(toastMessages.dispatchEdited);
              handleClose();
            }
          } catch (err) {
            toast.error(
              GetError(err?.response?.data?.errors) || "something issue"
            );
          }
        }
      } else {
      }
    }
  };

  /* this method is use for genrating random number for return number and this only 1 to 9 and A-Z charactor */
  const handleOrderTypeChange = (e) => {
    setOrderNoError("");
    if (e.target.value == "Delivery") {
      _DispatchOrderStateData({
        ...DispatchOrderStateData,
        type: e.target.value,
        orderNo: RandomeReturnNumberGenrate("DOO", 2, "/", 6),
      });
    } else {
      _DispatchOrderStateData({
        ...DispatchOrderStateData,
        type: e.target.value,
        orderNo: RandomeReturnNumberGenrate("COO", 2, "/", 6),
      });
    }
    clearErrors("radio");
  };

  /* This method is used to assign a value to the state of a client Name. */
  const handleClientNameChange = (event, values) => {
    _DispatchOrderStateData({
      ...DispatchOrderStateData,
      name: "",
      email: "",
      phone: "",
    });
    clearErrors("client");
    allOnChangeHandler("clientId", values?.id);
    setTimeout(() => {
      allOnChangeHandler("projectId", null);
      setValue("project", null);
    }, 200);

    setProjectArray(values.projects ? values.projects : []);
  };

  /* This method is used to assign a value to the state of a Project Name. */
  const handleProjectNameChange = async (event, values) => {
    if (values) {
      _DispatchOrderStateData({
        ...DispatchOrderStateData,
        street: values?.address?.street1 || "",
        town: values?.address?.city || "",
        country: values?.address?.country || "",
        postcode: values?.address?.zip || "",
        name: "",
        email: "",
        phone: "",
      });
      clearErrors("project");
      allOnChangeHandler("projectId", values?.id);
      setValue("projectManager", null);
      try {
        await dispatch(getAllItemsByProjectId(values?.id)).unwrap();
      } catch (error) {
        toast.error(GetError(error?.response?.data?.errors) || "something issue");
      }
      setProjectManagersArray(values.users ? values.users : []);
      setFilteredItemsArray([]);
      setSelectedItemsArray([]);
      setSelectedDepot();
    }
  };

  /* This method is used to assign a value to the state of a Project Managger Name. */
  const handleProjectManagerNameChange = (event, values) => {
    _DispatchOrderStateData({
      ...DispatchOrderStateData,
      name: `${values?.firstName} ${values?.lastName}` || "",
      email: values?.email || "",
      phone: values?.phoneNumber || "",
      projectManagerId: values?.userId
    });
    clearErrors("projectManager");
  };

  /* This method is used to assign a value to the state of a Depot Name. */
  const handleDepotNameChange = (target) => {
    setSelectedDepot(target);
    setDepotSelectError("");
    allOnChangeHandler("depotId", target);
    let found = {
      ...allItemsByDepotId.find(function (obj) {
        return obj.depotId == target;
      }),
    };
    found.items = found?.items.map((item) => {
      return {
        ...item,
        condition: lookupValues?.find((v) => v.id === item.conditionId)?.name,
        storage: lookupValues?.find((v) => v.id === item.storageId)?.name,
      };
    });
    setFilteredItemsArray(found?.items);
    setSelectedItemsArray([]);
  };

  /* This method is used to assign a value to the state of a search value. */
  const handleSearchInputChange = (e) => {
    const searchText = e.target.value;
    setSearchValue(searchText);
  };

  /* This method is used to clear a value to the state of a search value. */
  const handleClearSearchText = () => {
    handleSearchInputChange({ target: { value: "" } });
  };

  /* This method is used to add multiple item fileds */
  const handleAddItemClick = (itemId, index, itemObject) => {
    const cloneFilteredData = _.cloneDeep(filteredItemsArray);
    let FindIncrementData = _.findIndex(
      cloneFilteredData,
      (d) => d.id === itemId
    );

    let loadingUnit = cloneFilteredData[FindIncrementData]["loadingUnit"];

    let cloneSelectedItemsArray = _.cloneDeep(selectedItemsArray);
    let FindLoadingDatandex = _.findIndex(
      cloneSelectedItemsArray,
      (d) => d.id === itemId
    );

    if (loadingUnit > 0) {
      setHighlightedRow(null);
      if (FindLoadingDatandex !== -1) {
        cloneSelectedItemsArray[FindLoadingDatandex]["quantity"] =
          (cloneSelectedItemsArray[FindLoadingDatandex]?.quantity || 0) +
          cloneFilteredData[FindIncrementData].loadingUnit;
        setSelectedItemsArray([...cloneSelectedItemsArray]);
      } else {
        let newData = {
          ...itemObject,
          quantity: cloneFilteredData[FindIncrementData].loadingUnit,
        };
        setSelectedItemsArray([...cloneSelectedItemsArray, { ...newData }]);
      }
      cloneFilteredData[FindIncrementData]["availableQty"] =
        (cloneFilteredData[FindIncrementData]?.availableQty || 0) -
        (cloneFilteredData[FindIncrementData]?.loadingUnit || 0);
      cloneFilteredData[FindIncrementData]["loadingUnit"] = 0;
      let newOne = _.filter(cloneFilteredData, (d) => d.availableQty);
      setFilteredItemsArray([...newOne]);
    } else {
      setHighlightedRow(index);
    }
  };

  /* This method is used to add multiple item fileds */
  const handleKeyPress = (e) => {
    const keyCode = e.which || e.keyCode;
    if (keyCode === 189 || keyCode === 109) {
      e.preventDefault();
    }
  };

  const handleRemoveItemIconClickFromTable = (item) => {
    const cloneFilteredData = _.cloneDeep(filteredItemsArray);
    let FindIncrementData = _.findIndex(
      cloneFilteredData,
      (d) => d.id === item?.id
    );

    let cloneSelectedItemsArray = _.cloneDeep(selectedItemsArray);
    let FindLoadingDatandex = _.findIndex(
      cloneSelectedItemsArray,
      (d) => d.id === item?.id
    );

    if (FindIncrementData !== -1) {
      cloneFilteredData[FindIncrementData]["availableQty"] =
        cloneFilteredData[FindIncrementData]["availableQty"] +
        cloneSelectedItemsArray[FindLoadingDatandex]["quantity"];
      setFilteredItemsArray([...cloneFilteredData]);
    } else {
      let newone = {
        ...cloneSelectedItemsArray[FindLoadingDatandex],
        loadingUnit: 0,
        availableQty: cloneSelectedItemsArray[FindLoadingDatandex]["quantity"],
      };
      setFilteredItemsArray([...filteredItemsArray, { ...newone }]);
    }
    let filterData = _.filter(selectedItemsArray, (d) => d.id !== item?.id);
    setSelectedItemsArray([...filterData]);
  };

  const handleRemoveQuantityonedit = (quantity, itemId) => {
    const cloneSelectedItemsArray = _.cloneDeep(selectedItemsArray);
    let findInderselectedArray = _.findIndex(
      cloneSelectedItemsArray,
      (d) => d.id === itemId.id
    );

    cloneSelectedItemsArray[findInderselectedArray]["quantity"] = parseInt(
      quantity || 0
    );

    const clonefilteredItemsArray = _.cloneDeep(filteredItemsArray);
    let findInderCloneFilteredItemsArray = _.findIndex(
      clonefilteredItemsArray,
      (d) => d.id === itemId.id
    );

    if (findInderCloneFilteredItemsArray !== -1) {
      let quantitys = parseInt(
        clonefilteredItemsArray[findInderCloneFilteredItemsArray]
          ?.availableQty +
        parseInt(itemId?.quantity || 0) -
        parseInt(quantity || 0)
      );
      let orgQuantity = parseInt(
        clonefilteredItemsArray[findInderCloneFilteredItemsArray]
          ?.availableQty + parseInt(itemId?.quantity || 0)
      );
      if (orgQuantity >= parseInt(quantity || 0))
        if (Math.sign(quantitys) === 1) {
          clonefilteredItemsArray[
            findInderCloneFilteredItemsArray
          ].availableQty = quantitys;
          clonefilteredItemsArray[
            findInderCloneFilteredItemsArray
          ].orgQuantity = orgQuantity;
          setFilteredItemsArray([...clonefilteredItemsArray]);
          if (orgQuantity > parseInt(quantity || 0)) {
            setSelectedItemsArray([...cloneSelectedItemsArray]);
          }
        } else if (Math.sign(quantitys) == 0) {
          let data = _.filter(filteredItemsArray, (d) => d.id !== itemId?.id);
          setFilteredItemsArray([...data]);
          setSelectedItemsArray([...cloneSelectedItemsArray]);
        } else {
        }
    } else {
      let quantitys = itemId.quantity - quantity;
      if (Math.sign(quantitys) === 1) {
        let newArray = {
          ...itemId,
          availableQty: quantitys,
          loadingUnit: 0,
          orgQuantity: itemId.quantity,
        };
        if (newArray.orgQuantity > parseInt(quantity || 0)) {
          setSelectedItemsArray([...cloneSelectedItemsArray]);
        }
        setFilteredItemsArray([...filteredItemsArray, { ...newArray }]);
      }
    }
  };

  const sortProjectArray = _.orderBy(
    _.map(projectArray, (d) => {
      return {
        ...d,
        newName: d?.name || d?.projectName,
      };
    }),
    ["newName"],
    ["asc"]
  );

  const sortProjectManagersArray = _.orderBy(
    _.map(projectManagersArray, (d) => {
      return {
        ...d,
        newName: d.firstName + " " + d.lastName,
      };
    }),
    ["newName"],
    ["asc"]
  );

  if(loader){
    return <Loader/>
  }

  return (
    <>
      <div className="dispatch-form">
        <form onSubmit={handleSubmit(onDispatchOrderFormSubmitHandler)}>
          <div className="row" style={{ height: 70 }}>
            <div className="col-md-7">
              <div className="row d-flex align-items-center">
                <div className="col-md-4 d-flex flex-column">
                  <TextField
                    label="ORDER NO."
                    variant="standard"
                    id="standard-basic"
                    style={{ height: 42, width: "100%" }}
                    value={DispatchOrderStateData.orderNo}
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true,
                    }}
                  />
                  {/* <span className="errors-msg-shown-for-goodsin">{orderNoError && orderNoError}</span> */}
                </div>

                <div className="col-md-8 d-flex flex-column justify-content-end order-type-radio-buttons-class">
                  <div className="d-flex">
                    <div className="custom-radio">
                      <input
                        className="me-2 radio-btn-style"
                        type="radio"
                        id="type1"
                        name="orderType"
                        value="Delivery"
                        onChange={(e) => handleOrderTypeChange(e)}
                        checked={DispatchOrderStateData.type == "Delivery"}
                      />
                      <label
                        className="me-3 radio-buttons-input-style"
                        style={{ position: "initial" }}
                        htmlFor="type1"
                      >
                        DELIVERY
                      </label>
                    </div>
                    <div className="custom-radio">
                      <input
                        className="me-2"
                        type="radio"
                        id="type2"
                        name="orderType"
                        value="ClientCollection"
                        onChange={(e) => handleOrderTypeChange(e)}
                        checked={
                          DispatchOrderStateData.type == "ClientCollection"
                        }
                      />
                      <label
                        className="me-2 radio-buttons-input-style"
                        style={{ position: "initial" }}
                        htmlFor="type2"
                      >
                        COLLECTION
                      </label>
                    </div>
                  </div>
                  <div>
                    <span className="errors-msg-shown-for-goodsin">
                      {orderNoError && orderNoError}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                renderOption={(props, option) => {
                  return (<li {...props} key={option.id}>{option.name}</li>)
                }}
                options={_.orderBy(
                  allClientsWithProjectsWithManagers,
                  ["name"],
                  ["asc"]
                )}
                getOptionLabel={(item) =>
                  typeof item.name == "string" ? item.name : ""
                }
                onChange={handleClientNameChange}
                defaultValue={{
                  name: editData?.clientName || "",
                }}
                disabled={client?.name ? true : false}
                renderInput={(params) => (
                  <TextField
                    {...register("client")}
                    {...params}
                    label="CLIENT"
                    variant="standard"
                    id="standard-basic"
                    style={{ height: 42 }}
                  />
                )}
              />
              <span className="errors-msg-shown-for-goodsin">
                {errors.client?.message && errors.client?.message}
              </span>
            </div>
          </div>

          <div className="row " style={{ height: 70 }}>
            <div className="col-md-6">
              <Autocomplete
                disablePortal
                ref={projectRef}
                id="combo-box-demo"
                key={DispatchOrderStateData["clientId"]}
                options={sortProjectArray}
                getOptionLabel={(item) => {
                  if (item.name && typeof item.name == "string") {
                    return item.name;
                  }
                  if (item.projectName && typeof item.projectName == "string") {
                    return item.projectName;
                  }
                  return "";
                }}
                defaultValue={{ name: editData?.projectName || [] }}
                onChange={handleProjectNameChange}
                renderInput={(params) => (
                  <TextField
                    {...register("project")}
                    {...params}
                    label="PROJECT"
                    variant="standard"
                    id="standard-basic"
                    style={{ height: 42 }}
                  />
                )}
              />
              <span className="errors-msg-shown-for-goodsin">
                {errors.project?.message && errors.project?.message}
              </span>
            </div>

            <div className="col-md-6">
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                options={sortProjectManagersArray}
                key={DispatchOrderStateData["projectId"]}
                onChange={handleProjectManagerNameChange}
                defaultValue={{
                  firstName: editData?.projectManager?.firstName || "",
                  lastName: editData?.projectManager?.lastName || "",
                }}
                getOptionLabel={(item) =>
                  item.firstName || item.lastName
                    ? item.firstName + " " + item.lastName
                    : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...register("projectManager")}
                    {...params}
                    label="PROJECT MANAGER"
                    variant="standard"
                    id="standard-basic"
                    style={{ height: 42 }}
                  />
                )}
              />
              <span className="errors-msg-shown-for-goodsin">
                {errors.projectManager?.message &&
                  errors.projectManager?.message}
              </span>
            </div>
          </div>

          <div className="row mt-2">
            <span className="due-time-text-style">
              DELIVERY DUE / COLLECTION PICK UP
            </span>
          </div>

          <div
            className="row d-flex justify-content-center align-items-center"
            style={{ height: 70 }}
          >
            <div className="col-md-6">
              <div className="row d-flex">
                <div className="col-md-6">
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Date"
                      value={DispatchOrderStateData.dueDate}
                      onChange={(newValue) =>
                        allOnChangeHandler("dueDate", newValue)
                      }
                      className="DatePickerOutline"
                      variant="standard"
                      id="standard-basic"
                      inputFormat="dd-MM-yyyy"
                      format="dd.MM.yyyy"
                    />
                  </LocalizationProvider>
                </div>
                <div className="col-md-6">
                  <select
                    value={DispatchOrderStateData.time1}
                    onChange={({ target }) =>
                      allOnChangeHandler("time1", target.value)
                    }
                    style={{
                      textAlign: "center",
                      borderTop: 0,
                      borderLeft: 0,
                      borderRight: 0,
                      borderBottom: "1px solid #5e89e7",
                      height: 42,
                    }}
                  >
                    {[...Array(24)].map((d, index) => {
                      const formattedValue = index.toString().padStart(2, "0");
                      return (
                        <option key={index} value={index}>
                          {/* {index} */}
                          {formattedValue}
                        </option>
                      );
                    })}
                  </select>
                  :
                  <select
                    value={DispatchOrderStateData.time2}
                    onChange={({ target }) =>
                      allOnChangeHandler("time2", target.value)
                    }
                    style={{
                      textAlign: "center",
                      marginLeft: 3,
                      borderTop: 0,
                      borderLeft: 0,
                      borderRight: 0,
                      borderBottom: "1px solid #5e89e7",
                      height: 42,
                    }}
                  >
                    {[...Array(60)].map((d, index) => {
                      const formattedValue = index.toString().padStart(2, "0");
                      if (index === 0) {
                        return <option key={index}>{"00"}</option>;
                      }
                      // return <option key={index}>{index}</option>;
                      return (
                        <option key={index} value={index}>
                          {formattedValue}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
            <div className="col-md-6"></div>
          </div>

          <div className="d-flex mt-2">
            <span className="due-time-text-style">DELIVERY ADDRESS</span>
            {DispatchOrderStateData.type == "ClientCollection" && (
              <span className="due-time-text-style ms-2"> (OPTIONAL)</span>
            )}
          </div>

          <div className="row m-0" style={{ height: 70 }}>
            <TextField
              className="depot-form-textfields-style"
              onChange={({ target }) => streetChange(target.value)}
              name="street"
              label="STREET"
              variant="standard"
              value={DispatchOrderStateData.street || ""}
            />
            <span className="errors-msg-shown-for-goodsin depotform-street-class">
              {addressError?.street || ""}
            </span>
          </div>

          <div className="row" style={{ height: 70 }}>
            <div className="col-md-4">
              <TextField
                className="depot-form-textfields-style"
                onChange={({ target }) => townChange(target.value)}
                label="TOWN"
                variant="standard"
                value={DispatchOrderStateData.town || ""}
              />
              <div className="errors-msg-shown-for-goodsin">
                {addressError?.town || ""}
              </div>
            </div>

            <div className="col-md-4">
              <TextField
                className="depot-form-textfields-style"
                id="standard-basic"
                label="COUNTRY"
                onChange={({ target }) => countryChange(target.value)}
                variant="standard"
                value={DispatchOrderStateData.country || ""}
              />
              <div className="errors-msg-shown-for-goodsin">
                {addressError?.country || ""}
              </div>
            </div>
            <div className="col-md-4">
              <TextField
                className="depot-form-textfields-style"
                onChange={({ target }) => postcodeChange(target.value)}
                value={DispatchOrderStateData.postcode || ""}
                id="standard-basic"
                label="POSTCODE"
                variant="standard"
              />
              <div className="errors-msg-shown-for-goodsin">
                {addressError?.postcode || ""}
              </div>
            </div>
          </div>

          <div className="row mt-2">
            <span className="due-time-text-style">CONTACT PERSON</span>
          </div>

          <div className="row" style={{ height: 70 }}>
            <div className="col-md-3">
              <TextField
                className="depot-form-textfields-style"
                // {...register("name")}
                onChange={({ target }) =>
                  AllChangeHandler("name", target.value)
                }
                value={DispatchOrderStateData.name || ""}
                id="standard-basic"
                label="NAME"
                variant="standard"
              />
              <div className="errors-msg-shown-for-goodsin">
                {addressError.name && addressError.name}
              </div>
            </div>

            <div className="col-md-4">
              <TextField
                className="depot-form-textfields-style"
                // {...register("email")}
                onChange={({ target }) =>
                  allOnChangeHandler("email", target.value)
                }
                value={DispatchOrderStateData.email || ""}
                id="standard-basic"
                label="EMAIL"
                variant="standard"
              />
              <div className="errors-msg-shown-for-goodsin">
                {addressError.email && addressError.email}
              </div>
            </div>
            <div className="col-md-4">
              <TextField
                type="string"
                className="depot-form-textfields-style"
                // {...register("phone")}
                onChange={({ target }) =>
                  allOnChangeHandler("phone", target.value)
                }
                value={DispatchOrderStateData.phone || ""}
                id="standard-basic"
                label="PHONE"
                variant="standard"
              />
              <div className="errors-msg-shown-for-goodsin">
                {addressError.phone && addressError.phone}
              </div>
            </div>
          </div>

          <div className="row mt-2">
            <span className="due-time-text-style">
              DRIVER DETAILS (OPTIONAL)
            </span>
          </div>
          <div className="row" style={{ height: 70 }}>
            <div className="col-md-3">
              <TextField
                className="depot-form-textfields-style"
                id="standard-basic"
                label="VRN"
                value={DispatchOrderStateData.vrn}
                onChange={({ target }) =>
                  allOnChangeHandler("vrn", target.value)
                }
                variant="standard"
              />
            </div>

            <div className="col-md-3">
              <TextField
                className="depot-form-textfields-style"
                value={DispatchOrderStateData.vehicleModel}
                id="standard-basic"
                label="VEHICLE TYPE"
                onChange={({ target }) =>
                  allOnChangeHandler("vehicleModel", target.value)
                }
                variant="standard"
              />
            </div>
            <div className="col-md-3">
              <TextField
                type="string"
                className="depot-form-textfields-style"
                value={DispatchOrderStateData.driverName}
                id="standard-basic"
                label="DRIVER NAME"
                onChange={({ target }) =>
                  allOnChangeHandler("driverName", target.value)
                }
                variant="standard"
              />
            </div>

            <div className="col-md-3">
              <TextField
                type="string"
                className="depot-form-textfields-style"
                value={DispatchOrderStateData.driverContact}
                id="standard-basic"
                label="DRIVER CONTACT"
                onChange={({ target }) =>
                  allOnChangeHandler("driverContact", target.value)
                }
                variant="standard"
              />
              <div className="errors-msg-shown-for-goodsin">
                {addressError.driverContact && addressError.driverContact}
              </div>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-md-2">
              <span className="req-text-style">REQUIREMENTS</span>
            </div>
            <div className="col-md-10">
              <span className="req-msg-text-style">
                All items must belong to the same depot. For deliveries from
                multiple depots, make serarate dispatch orders
              </span>
            </div>
          </div>

          <div className="row align-items-center mt-2">
            <div className="col-md-4 d-flex flex-column">
              <FormControl variant="standard" style={{ width: "100%" }}>
                <InputLabel id="demo-simple-select-standard-label">
                  SELECT DEPOT HERE
                </InputLabel>
                <Select
                  labelId="demo-simple-select-standard-label"
                  id="demo-simple-select-standard"
                  onChange={({ target }) => handleDepotNameChange(target.value)}
                  label="SELECT DEPOT HERE"
                  value={selectedDepot || ""}
                >
                  {allItemsByDepotId.length > 0 &&
                    _.orderBy(allItemsByDepotId, ["name"], ["asc"]).map(
                      (item, index) => {
                        return (
                          <MenuItem key={index} value={item.depotId}>
                            {item.name}
                          </MenuItem>
                        );
                      }
                    )}
                </Select>
                <div>
                  <span className="errors-msg-shown-for-goodsin">
                    {depotSelectError && depotSelectError}
                  </span>
                </div>
              </FormControl>
            </div>
            <div
              className="col-md-8"
              style={{ position: "relative", marginTop: 18 }}
            >
              <input
                style={{ position: "relative", width: "100%" }}
                className="search-input-for-depot-personal-tab"
                type="text"
                placeholder="Search by item ..."
                value={searchValue}
                onChange={handleSearchInputChange}
              />
              {searchValue !== "" ? (
                <Close
                  style={{ cursor: "pointer" }}
                  onClick={handleClearSearchText}
                  className="search-icon-depotpersonal-style"
                />
              ) : (
                <Search className="search-icon-depotpersonal-style" />
              )}
            </div>
          </div>

          <div className="mt-2">
            {newFilteredArray?.length > 0 ? (
              _.orderBy(newFilteredArray || [], ["itemNumber"], ["asc"])?.map(
                (item, index) => {
                  return (
                    <div
                      style={{ flex: 1 }}
                      key={index}
                      className="d-flex flex-row individual-dispatch-table-item-style mt-2 pt-1 pb-1"
                    >
                      <div
                        style={{ flex: 3 }}
                        className=" dashboarddepotName-value-style-desc"
                      >
                        {item.itemNumber}
                      </div>
                      <div style={{ flex: 2 }} className="">
                        {item.condition}
                      </div>
                      <div
                        style={{ flex: 3 }}
                        className="dashboarddepotName-value-style-desc"
                      >
                        {item.description}
                      </div>
                      <div style={{ flex: 2 }} className="">
                        {item?.availableQty - (item?.loadingUnit || 0)}
                      </div>
                      <div style={{ flex: 2 }} className="">
                        <input
                          className="search-input-for-depot-personal-tab unit-item-range-style"
                          onChange={({ target }) => {
                            if (isNumber(target.value)) {
                              setHighlightedRow(null);
                              const cloneAvailableData =
                                _.cloneDeep(filteredItemsArray);
                              let FindIncrementData = _.findIndex(
                                cloneAvailableData,
                                (d) => d.itemNumber === item?.itemNumber
                              );
                              cloneAvailableData[FindIncrementData][
                                "loadingUnit"
                              ] = parseInt(target.value);
                              let Value =
                                parseInt(
                                  cloneAvailableData[FindIncrementData]
                                    ?.availableQty
                                ) -
                                parseInt(
                                  cloneAvailableData[FindIncrementData]
                                    ?.loadingUnit || 0
                                );
                              if (Value >= 0) {
                                setFilteredItemsArray([...cloneAvailableData]);
                              } else {
                                cloneAvailableData[FindIncrementData][
                                  "loadingUnit"
                                ] = parseInt(
                                  cloneAvailableData[FindIncrementData]
                                    ?.availableQty
                                );
                                setFilteredItemsArray([...cloneAvailableData]);
                              }
                            }
                          }}
                          onKeyDown={handleKeyPress}
                          placeholder="Units"
                          type="number"
                          value={item?.loadingUnit || ""}
                          maxLength="3"
                          style={{
                            width: "70%",
                            height: 25,
                            marginTop: 5,
                            border:
                              highlightedRow == index ? "1px solid red" : "",
                          }}
                        />
                      </div>
                      <div style={{ flex: 2 }} className="">
                        <button
                          type="button"
                          style={{ cursor: "pointer" }}
                          className="add-item-button-style"
                          onClick={() =>
                            handleAddItemClick(item.id, index, item)
                          }
                        >
                          <img
                            src={IconNewRecord}
                            className="me-1"
                            width={15}
                            height={15}
                            alt="new"
                          />
                          ADD
                        </button>
                      </div>
                    </div>
                  );
                }
              )
            ) : (
              <div>No records found</div>
            )}
          </div>

          <div className="mt-3">
            <Divider
              variant="fullWidth"
              component="div"
              style={{
                backgroundColor: "blue",
                height: "2px",
                marginBottom: "1rem",
                marginTop: "1rem",
              }}
            />
            <table className="table goodsin-table table-borderless p-0">
              <thead>
                <tr>
                  <th>
                    <span className="items-table-header-count">
                      {selectedItemsArray?.length || 0}
                    </span>
                    <span className="items-table-text"> ITEMS</span>
                  </th>
                  <th>
                    <span className="items-table-header-count">
                      {damageCount}
                    </span>
                    <span className="items-table-text"> DAMAGED</span>
                  </th>
                  <th>DESCRIPTION</th>
                  <th>
                    <span className="items-table-header-count">
                      {unitsCount}
                    </span>
                    <span className="items-table-text"> UNITS</span>
                  </th>
                  <th>
                    <span className="items-table-text"> LOADED QTY</span>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="goods-table-body">
                {selectedItemsArray &&
                  selectedItemsArray[0] &&
                  _.orderBy(
                    selectedItemsArray || [],
                    ["itemNumber"],
                    ["asc"]
                  ).map((item, index) => {
                    return (
                      <tr key={index} className="goodsin-items-tr">
                        <td>{item.itemNumber}</td>
                        <td>{item.condition}</td>
                        <td>{item.description}</td>
                        <td>
                          <input
                            className="search-input-for-depot-personal-tab unit-item-range-style"
                            onChange={({ target }) =>
                              handleRemoveQuantityonedit(target.value, item)
                            }
                            onKeyDown={handleKeyPress}
                            placeholder="Units"
                            type="number"
                            value={item.quantity || ""}
                            style={{
                              width: "70%",
                              height: 25,
                              marginTop: 5,
                              border:
                                highlightedRow == index ? "1px solid red" : "",
                            }}
                          />
                        </td>
                        <td>{item?.loadedQuantity || 0}</td>
                        {!item?.loadedQuantity && (
                          <td>
                            <div
                              className="d-flex justify-content-center align-items-center"
                              onClick={() =>
                                handleRemoveItemIconClickFromTable(item)
                              }
                            >
                              <RemoveCircleOutlineOutlinedIcon
                                className="remove-item-icon-color"
                                style={{ cursor: "pointer", color: "red" }}
                                fontSize="20"
                              />
                              <span
                                className="ms-1 remove-item-label-color"
                                style={{
                                  fontFamily: "Montserrat-Regular",
                                  fontSize: 10,
                                  cursor: "pointer",
                                  color: "red",
                                }}
                              >
                                REMOVE
                              </span>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div
            className="d-flex flex-row justify-content-between"
            style={{ marginTop: 10, marginBottom: 20 }}
          >
            <Button
              variant="outlined"
              className="btn-bootstrap-submit-style"
              style={{ color: "red", borderColor: "red" }}
              startIcon={<CancelIcon />}
              onClick={() => handleClose()}
            >
              Cancel
            </Button>
            {!editData ? (
              <Button
                disabled={addNewDispatchOrderLoadingState ? true : false}
                variant="outlined"
                className={`btn-bootstrap-submit-style ${addNewDispatchOrderLoadingState ? "fade-out" : ""
                  }`}
                startIcon={<CheckCircleIcon />}
                type="submit"
              >
                Submit
              </Button>
            ) : (
              <Button
                variant="outlined"
                className="btn-bootstrap-submit-style"
                startIcon={<CheckCircleIcon />}
                type="submit"
              >
                Save
              </Button>
            )}
          </div>
        </form>
      </div>
    </>
  );
};
